import { useEffect, useState } from 'react';
import { eventBus } from '@/shared/EventBus';
import type { HalloweenBossView } from '@/net/api';
import { Icon } from './Icon';

/** A compact boss meter, with the same separate teleport control as Atlantis. */
export function HalloweenBossHud() {
  const [view, setView] = useState<HalloweenBossView | null>(null);
  const [message, setMessage] = useState('Przygotowujemy cukierkową polanę…');
  const [reward, setReward] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [offset, setOffset] = useState(0);
  useEffect(() => eventBus.on('halloweenBoss:view', event => {
    setView(event.view);
    setMessage(event.message ?? '');
    if (event.view) setOffset(event.view.serverNow - Date.now());
    if (event.view?.fight) setReward(0);
    else if (event.reward) setReward(event.reward);
  }), []);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const collected = view?.status === 'cooldown' || reward ? view?.goal ?? 200 : view?.fight?.collected ?? 0;
  const goal = view?.goal ?? 200;
  const left = Math.max(0, Math.ceil(((view?.nextAvailableAt ?? now) - now - offset) / 1000));
  const cooldown = `${Math.floor(left / 3600)}:${String(Math.floor(left % 3600 / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
  const isCooling = view?.status === 'cooldown';
  const isInactive = view?.status === 'inactive';
  const percent = Math.max(0, Math.min(100, collected / goal * 100));

  return <>
    <button
      type="button"
      onClick={() => eventBus.emit('halloweenBoss:exit', {})}
      className="absolute top-3 right-3 z-40 pointer-events-auto bg-white/95 hover:bg-white active:scale-95 rounded-2xl shadow-lg px-3 py-2 flex items-center gap-1.5 transition-all"
    >
      <Icon src="/ui/ui_btn_teleport.webp" fallback="" alt="" className="w-6 h-6" />
      <span className="text-xs sm:text-sm font-bold text-meadow-700">Opuść arenę</span>
    </button>

    <section aria-label="Postęp walki z Wielką Dynią" className="absolute top-16 left-1/2 z-40 w-[min(88vw,420px)] -translate-x-1/2 sm:top-3 sm:w-[min(52vw,480px)] pointer-events-none">
      {!isInactive && <div className="relative h-7 rounded-full border-2 border-[#fff8e9] bg-[#e9dbdf]/95 shadow-[0_3px_12px_rgba(91,57,62,0.2)]" role="progressbar" aria-label="Zebrane cukierki" aria-valuemin={0} aria-valuemax={goal} aria-valuenow={collected}>
          <div className="absolute inset-0 overflow-hidden rounded-full">
            <div className="h-full rounded-full bg-gradient-to-r from-[#ffb661] via-[#ff9c47] to-[#ef8138] transition-[width] duration-300" style={{ width: `${percent}%` }} />
          </div>
          <span className={`absolute inset-0 flex items-center px-3 text-[11px] font-extrabold tabular-nums text-[#593a58] ${percent < 50 ? 'justify-end' : 'justify-start'}`}>{collected} / {goal}</span>
          <span data-candy-meter-tip="" className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transition-[left] duration-300" style={{ left: `clamp(18px, ${percent}%, calc(100% - 18px))` }}>
            <Icon src="/ui/ui_candy.webp" fallback="🍬" alt="" className="h-10 w-10 drop-shadow-sm" />
          </span>
      </div>}
    </section>

    {isCooling && <aside aria-label="Dostępność bossa" className="absolute top-40 right-3 sm:top-24 z-40 rounded-2xl border border-[#e5c8ab] bg-[#fff8e9]/95 px-3 py-2 text-center shadow-sm pointer-events-auto">
      <p className="text-[10px] font-bold text-[#937464]">Dynia wróci za</p>
      <p className="text-sm font-extrabold tabular-nums text-[#6b4977]">{cooldown}</p>
      {left === 0 && <button type="button" className="mt-1 text-xs font-bold text-[#7d4b85] underline" onClick={() => eventBus.emit('halloweenBoss:retry', {})}>Zagraj znowu</button>}
    </aside>}

    {message && <div className="absolute bottom-20 left-4 right-4 z-40 mx-auto max-w-md rounded-2xl bg-white/95 p-3 text-center text-sm text-purple-900 pointer-events-auto" role="status">
      {message}
      {!isInactive && <button type="button" className="ml-2 underline font-bold" onClick={() => eventBus.emit('halloweenBoss:retry', {})}>Spróbuj ponownie</button>}
    </div>}

    {reward > 0 && <div className="absolute bottom-5 right-3 z-40 flex items-center gap-2 rounded-2xl border border-orange-200 bg-[#fff3d7]/95 px-4 py-3 shadow-lg pointer-events-none" role="status">
      <Icon src="/ui/ui_candy.webp" fallback="" alt="" className="h-9 w-9" />
      <div>
        <p className="text-sm font-extrabold text-[#85502b]">Brawo! +{reward} cukierków</p>
        <p className="text-[10px] text-[#8e6c61]">Nagroda jest już w Twoim portfelu</p>
      </div>
    </div>}
  </>;
}
